import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { Observable } from 'rxjs';
import { BucketService } from '../../../services/bucket.service';
import { ChatService } from '../../../services/chat.service';
import { DataSourcesService } from '../../../services/data-sources.service';
import { Tutor, TutorService, TutorTemplate } from '../../../services/tutor.service';
import { AgentOrbType, ORB_BY_TEMPLATE } from '../../agent-orb/agent-orb.component';
import { ORBI_IDLE } from '../../orbi-lab/orbi-lab.component';

/** Eine Feature-Pille unter einer Vorlagen-Kachel. `cls` wählt die
 *  Pillen-Farbe aus tutor-dialog.component.scss — die Farbe steht für die
 *  ART der Fähigkeit (z.B. "extern" = Anbindung an ein System außerhalb
 *  des Portals), nicht für den einzelnen Namen, damit verwandte Features
 *  über alle Vorlagen hinweg dieselbe Farbe tragen. */
interface Badge {
  key: string;
  cls:
    | 'extern'
    | 'didaktik'
    | 'uebung'
    | 'debatte'
    | 'twin'
    | 'personen'
    | 'wissen'
    | 'faq'
    | 'uebergabe'
    | 'verwaltung'
    | 'support';
}

const BADGES: Record<string, Badge[]> = {
  alpha_tutor: [
    { key: 'kursSync', cls: 'extern' },
    { key: 'didaktik', cls: 'didaktik' },
    { key: 'uebungsmodus', cls: 'uebung' },
    { key: 'debattiermodus', cls: 'debatte' },
    { key: 'twinLink', cls: 'twin' },
    { key: 'lernerprofile', cls: 'personen' },
    { key: 'wissensbasis', cls: 'wissen' },
  ],
  alpha_flexible: [
    { key: 'kursSync', cls: 'extern' },
    { key: 'didaktik', cls: 'didaktik' },
    { key: 'uebungsmodus', cls: 'uebung' },
    { key: 'debattiermodus', cls: 'debatte' },
    { key: 'twinLink', cls: 'twin' },
    { key: 'lernerprofile', cls: 'personen' },
    { key: 'promptBausteine', cls: 'verwaltung' },
    { key: 'curriculum', cls: 'didaktik' },
    { key: 'hausaufgabenPolicy', cls: 'didaktik' },
  ],
  alpha_dozent: [
    { key: 'tutorLink', cls: 'twin' },
    { key: 'weiterleitung', cls: 'uebergabe' },
    { key: 'eskalation', cls: 'uebergabe' },
    { key: 'faq', cls: 'faq' },
    { key: 'wissensbasis', cls: 'wissen' },
    { key: 'dozentVerwaltung', cls: 'verwaltung' },
  ],
  alpha_support: [
    { key: 'wissensbasis', cls: 'wissen' },
    { key: 'faq', cls: 'faq' },
    { key: 'webseiten', cls: 'extern' },
    { key: 'leadErfassung', cls: 'personen' },
    { key: 'emailTickets', cls: 'uebergabe' },
  ],
  alpha_assist: [
    { key: 'wissensbasis', cls: 'wissen' },
    { key: 'faq', cls: 'faq' },
    { key: 'lmsIntegration', cls: 'extern' },
    { key: 'firstLevel', cls: 'support' },
  ],
};

/** Vorlage, die beim Öffnen vorausgewählt ist und deren Kachel auf die
 *  Variantenwahl (Tutor vs. Flexible) führt — per Klick auf die Kachel
 *  UND per "Weiter", falls sie schon vorausgewählt war. */
const DRILLDOWN_TEMPLATE_NAME = 'alpha_tutor';

/** Reihenfolge der Kacheln auf Ebene 1. Vorlagen, die der creatable-
 *  Endpoint nicht liefert (z.B. abweichende Rechte pro Client), fallen
 *  einfach raus statt eine leere Kachel zu zeigen. */
const STAGE1_ORDER = ['alpha_tutor', 'alpha_dozent', 'alpha_support', 'alpha_assist'];

/** Reihenfolge der Kacheln auf Ebene 2 (Tutor-Variante), mit Einstufung
 *  für das Level-Badge. */
const VARIANT_ORDER: Array<{ name: string; level: 'starter' | 'pro' }> = [
  { name: 'alpha_tutor', level: 'starter' },
  { name: 'alpha_flexible', level: 'pro' },
];

type PickerStage = 'templates' | 'variant' | 'confirm';

/** Orbi-Abläufe je Agent auf Ebene 1 und 2, endlos wiederholt (Marcel,
 *  26.09.2026). IDs aus orbi-lab/vendor/sequences/registry.js,
 *  `ORBI_IDLE` = "normal schauen". */
const PICKER_ORB_SCRIPTS: Record<AgentOrbType, string[]> = {
  tutor: ['hop-zigzag', 'laugh', ORBI_IDLE],
  twin: ['idle-whistle', ORBI_IDLE],
  support: [ORBI_IDLE, 'giggle-bounce', ORBI_IDLE],
  assist: [ORBI_IDLE, 'hop-right', 'hop-left', 'laugh', ORBI_IDLE],
  flexible: ['hide-surprise', ORBI_IDLE],
};

@Component({
  selector: 'app-tutor-dialog',
  templateUrl: './tutor-dialog.component.html',
  styleUrl: './tutor-dialog.component.scss',
})
export class TutorDialogComponent implements OnInit {
  @Input() data?: Tutor;

  @Input() onComplete = (tutor: Tutor) => {};

  @ViewChild('nameInput') nameInputRef?: ElementRef<HTMLInputElement>;

  form;

  /** Templates shown in the create dropdown. This list comes from the
   *  backend's creatable endpoint, so per-client super-admin grants apply
   *  here while legacy templates remain usable only for existing agents. */
  creatableTemplates$: Observable<TutorTemplate[]> =
    this.tutorService.creatableTutorTemplates$;

  /** Snapshot des letzten Emits von `creatableTemplates$` — die drei-
   *  stufige Auswahl braucht synchrone Lookups (welche Vorlage gehört zu
   *  dieser id?, welche id gehört zu diesem Namen?), dafür reicht der
   *  Observable-Stream allein nicht. */
  private creatableTemplates: TutorTemplate[] = [];

  /** Welche der drei Ebenen der Create-Flow gerade zeigt. Bleibt für den
   *  Edit-Fall ungenutzt — dort rendert das Template weiterhin die
   *  schlichte, immer sichtbare Form. */
  stage: PickerStage = 'templates';

  /** Auf welche Ebene "Andere Vorlage" aus der Bestätigung zurückführt:
   *  Ebene 1, wenn direkt eine Nicht-Tutor-Vorlage gewählt wurde, sonst
   *  Ebene 2 (Tutor/Flexible kommen immer über die Variantenwahl). */
  private confirmReturnStage: PickerStage = 'templates';

  constructor(
    protected fb: FormBuilder,
    protected dialogRef: NbDialogRef<TutorDialogComponent>,
    protected filesService: BucketService,
    protected tutorService: TutorService,
    protected dataSourcesService: DataSourcesService,
    protected chatService: ChatService
  ) {
    this.form = fb.group({
      name: this.fb.control('', Validators.required),
      description: this.fb.control(''),
      template_id: this.fb.control('', Validators.required),
      data_source_ids: this.fb.control([]),
      widget_setting_id: this.fb.control(''),
    });

    this.form.controls.data_source_ids.setValue([]);
    this.dataSourcesService.listDataSources().subscribe();
    this.chatService.listChatSettings().subscribe();
  }

  ngOnInit() {
    if (this.data) {
      this.tutorService.listTutorTemplates().subscribe();
      this.form.patchValue(this.data as any);
      this.form.controls['template_id'].disable();
      return;
    }

    this.tutorService.listCreatableTutorTemplates().subscribe((templates) => {
      this.creatableTemplates = templates;
      const preselected =
        templates.find((t) => t.name === DRILLDOWN_TEMPLATE_NAME) || templates[0];
      if (preselected) {
        this.form.controls.template_id.setValue(preselected.id);
      }
    });
  }

  // -----------------------------------------------------------------------
  // Vorlagen-Auswahl (Create-Flow, drei Ebenen)
  // -----------------------------------------------------------------------

  get stage1Cards(): TutorTemplate[] {
    return STAGE1_ORDER.map((name) =>
      this.creatableTemplates.find((t) => t.name === name)
    ).filter((t): t is TutorTemplate => !!t);
  }

  get variantCards(): Array<{ template: TutorTemplate; level: 'starter' | 'pro' }> {
    return VARIANT_ORDER.map(({ name, level }) => {
      const template = this.creatableTemplates.find((t) => t.name === name);
      return template ? { template, level } : null;
    }).filter((c): c is { template: TutorTemplate; level: 'starter' | 'pro' } => !!c);
  }

  /** `variantCards` baut pro Change Detection neue Wrapper-Objekte — ohne
   *  trackBy verwarf `*ngFor` jedes Mal die Karten samt `app-orbi-lab`, die
   *  Orbis starteten neu und kamen nie bis zum ersten Ausdruck (Regel 18). */
  trackVariant(_: number, card: { template: TutorTemplate }): string {
    return card.template.id;
  }

  badgesFor(templateName: string): Badge[] {
    return BADGES[templateName] || [];
  }

  /** Welche Kacheln auf Ebene 1 gerade ihren Langtext zeigen. Ein Set statt
   *  eines Felds pro Karte, weil die Karten aus `stage1Cards` generiert
   *  werden und keinen eigenen Komponenten-State mitbringen. */
  private expandedTemplateIds = new Set<string>();

  isExpanded(templateId: string): boolean {
    return this.expandedTemplateIds.has(templateId);
  }

  /** `stopPropagation` ist Pflicht: der Toggle sitzt innerhalb der
   *  klickbaren Kachel, ohne sie würde jeder Klick auf "Mehr erfahren"
   *  zugleich die Vorlage auswählen (und bei Alpha Tutor die Ebene
   *  wechseln). */
  toggleDetails(templateId: string, event: Event) {
    event.stopPropagation();
    if (this.expandedTemplateIds.has(templateId)) {
      this.expandedTemplateIds.delete(templateId);
    } else {
      this.expandedTemplateIds.add(templateId);
    }
  }

  orbTypeFor(templateName: string): AgentOrbType {
    return ORB_BY_TEMPLATE[templateName] || 'tutor';
  }

  /** Feste Array-Instanzen aus `PICKER_ORB_SCRIPTS` — Regel 18 (CLAUDE.md). */
  orbScriptFor(templateName: string): string[] {
    return PICKER_ORB_SCRIPTS[this.orbTypeFor(templateName)];
  }

  isSelected(template: TutorTemplate): boolean {
    return template.id === this.form.controls.template_id.value;
  }

  /** Kachel-Klick auf Ebene 1. Führt bei der Tutor-Kachel sofort in die
   *  Variantenwahl — dieselbe Regel, die auch "Weiter" befolgt (siehe
   *  `goNext`), damit beide Wege nie auseinanderlaufen. */
  selectStage1(template: TutorTemplate) {
    this.form.controls.template_id.setValue(template.id);
    if (template.name === DRILLDOWN_TEMPLATE_NAME) {
      this.stage = 'variant';
    }
  }

  /** Kachel-Klick auf Ebene 2. Wählt nur aus, führt nicht weiter — das
   *  übernimmt "Weiter", damit man beide Varianten erst vergleichen kann. */
  selectVariant(template: TutorTemplate) {
    this.form.controls.template_id.setValue(template.id);
  }

  backToTemplates() {
    this.stage = 'templates';
    // Kam man mit "Alpha Flexible" zurück, zeigt Ebene 1 sonst keine
    // Kachel als ausgewählt — Flexible taucht dort gar nicht auf.
    const stillVisible = this.stage1Cards.some(this.isSelected.bind(this));
    if (!stillVisible) {
      const tutor = this.stage1Cards.find((t) => t.name === DRILLDOWN_TEMPLATE_NAME);
      if (tutor) this.form.controls.template_id.setValue(tutor.id);
    }
  }

  backFromConfirm() {
    this.stage = this.confirmReturnStage;
  }

  /** Impliziter Form-Submit = Enter im Namensfeld. Im Edit-Modus wie
   *  bisher speichern; im Create-Flow entspricht Enter dem
   *  "Erstellen"-Button der Bestätigungs-Ebene (goNext → onSubmit, leerer
   *  Name bekommt dort das Pflichtfeld-Feedback). Die Ebenen davor haben
   *  keine Textfelder — ihre Kacheln behandeln Enter selbst. Ein laufender
   *  Request wird nicht doppelt ausgelöst.
   *  Hinweis: Im Create-Flow greift normalerweise schon der globale
   *  `DialogEnterActionService` (klickt "Erstellen" und cancelt den
   *  impliziten Submit) — dieser Zweig ist der Fallback ohne ihn. */
  onFormSubmit() {
    if (this.tutorService.loading$.value) return;
    if (this.data) {
      this.onSubmit();
      return;
    }
    if (this.stage === 'confirm') {
      this.goNext();
    }
  }

  /** Einziger "Weiter"/"Erstellen"-Handler für den ganzen Create-Flow —
   *  welche Aktion er auslöst, hängt nur von der aktuellen Ebene ab. */
  goNext() {
    if (this.stage === 'templates') {
      const picked = this.stage1Cards.find((t) => this.isSelected(t));
      if (!picked) return;
      if (picked.name === DRILLDOWN_TEMPLATE_NAME) {
        this.stage = 'variant';
      } else {
        this.goToConfirm('templates');
      }
      return;
    }
    if (this.stage === 'variant') {
      this.goToConfirm('variant');
      return;
    }
    this.onSubmit();
  }

  private goToConfirm(from: PickerStage) {
    this.confirmReturnStage = from;
    this.stage = 'confirm';
    // Der Name-Fokus muss warten, bis Angular den *ngIf-Zweig gerendert
    // hat — ohne setTimeout existiert `nameInputRef` in diesem Tick noch
    // nicht im DOM.
    setTimeout(() => this.nameInputRef?.nativeElement.focus());
  }

  get confirmTemplate(): TutorTemplate | undefined {
    const id = this.form.controls.template_id.value;
    return this.creatableTemplates.find((t) => t.id === id);
  }

  get confirmOrbType(): AgentOrbType {
    return this.orbTypeFor(this.confirmTemplate?.name ?? '');
  }

  get canCreate(): boolean {
    return this.form.controls.name.valid;
  }

  onTemplateCardKeydown(event: KeyboardEvent, current: TutorTemplate) {
    this.handleCardKeydown(event, this.stage1Cards, current, (t) => this.selectStage1(t));
  }

  onVariantCardKeydown(event: KeyboardEvent, current: TutorTemplate) {
    const templates = this.variantCards.map((v) => v.template);
    this.handleCardKeydown(event, templates, current, (t) => this.selectVariant(t));
  }

  /** Roving-Tabindex-Navigation für die beiden Radiogroups (4 Kacheln auf
   *  Ebene 1, 2 auf Ebene 2). Grabbt die Geschwister-Elemente direkt aus
   *  dem DOM statt über ViewChildren-Indizes zu buchhalten — bei zwei
   *  Aufrufstellen in einer Komponente ist das der einfachere Weg. */
  private handleCardKeydown(
    event: KeyboardEvent,
    cards: TutorTemplate[],
    current: TutorTemplate,
    select: (t: TutorTemplate) => void
  ) {
    const index = cards.findIndex((c) => c.id === current.id);
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      select(current);
      return;
    }
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const nextIndex = (index + step + cards.length) % cards.length;
    const next = cards[nextIndex];
    select(next);
    const group = (event.currentTarget as HTMLElement).parentElement;
    group?.querySelectorAll<HTMLElement>('[role="radio"]')[nextIndex]?.focus();
  }

  // -----------------------------------------------------------------------

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.data) {
      this.tutorService
        .updateTutor({
          ...this.data,
          ...(this.form.value as Tutor),
        })
        .subscribe((d) => {
          this.onComplete(d.data);
          this.dialogRef.close();
        });
      return;
    }

    this.tutorService
      .createTutor({
        ...(this.form.value as Tutor),
      })
      .subscribe((d) => {
        this.onComplete(d.data);
        this.dialogRef.close();
      });
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }

  getTemplateIdDescription() {
    const selectedId = this.form.get('template_id')?.value;
    const templateName = (
      this.tutorService.tutorTemplates$.value.find((t) => t.id === selectedId) ||
      this.tutorService.creatableTutorTemplates$.value.find((t) => t.id === selectedId)
    )?.name;
    // Before a template is picked, the lookup returns undefined and we'd
    // render the raw `enums.assistantTemplateDescription.undefined` key in
    // the help-popover. Fall back to a "pick a template first" hint key
    // so the tooltip stays useful.
    if (!templateName) {
      return 'enums.assistantTemplateDescription.empty';
    }
    return `enums.assistantTemplateDescription.${templateName}`;
  }
}
